import React, { useState, useCallback } from "react";
import MuiTextField, { type TextFieldProps } from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

export type SearchProps = TextFieldProps & {
  onClear?: () => void;
};

export function Search({
  value: controlledValue,
  onChange,
  onClear,
  placeholder = "Search…",
  variant = "outlined",
  slotProps,
  ...props
}: SearchProps) {
  const [internalValue, setInternalValue] = useState("");
  const isControlled = controlledValue !== undefined;
  const currentValue = isControlled ? controlledValue : internalValue;

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      if (!isControlled) setInternalValue(e.target.value);
      onChange?.(e);
    },
    [isControlled, onChange]
  );

  const handleClear = useCallback(() => {
    if (!isControlled) setInternalValue("");
    onClear?.();
  }, [isControlled, onClear]);

  return (
    <MuiTextField
      variant={variant}
      placeholder={placeholder}
      value={currentValue}
      onChange={handleChange}
      slotProps={{
        ...slotProps,
        input: {
          sx: {
            pl: "8px",
            pr: "4px",
            gap: 0,
            "& .MuiOutlinedInput-input": { pl: "8px" },
          },
          startAdornment: (
            <InputAdornment position="start" sx={{ mr: 0 }}>
              <SearchIcon sx={{ fontSize: 20, color: "text.placeholder" }} />
            </InputAdornment>
          ),
          endAdornment: currentValue ? (
            <InputAdornment position="end">
              <IconButton
                aria-label="clear search"
                onClick={handleClear}
                edge="end"
                size="small"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
          ...(typeof slotProps?.input === "object" ? slotProps.input : {}),
        },
      }}
      {...props}
    />
  );
}
